import { Card } from "../ui/card";
import { CalendarDays } from "lucide-react";

export const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
export const STATUS_STYLE = {
  pubblicata: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
  assegnato: "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
  completata: "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200",
  da_fatturare: "bg-red-600 text-white border-red-700 hover:bg-red-700",
  annullata: "bg-red-100 text-red-700 border-red-200 hover:bg-red-200 line-through",
};
const FALLBACK = "bg-slate-100 text-slate-700 border-slate-200";
export const hhmm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
export const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const EventChip = ({ ev, onOpen }) => (
  <button data-testid={`cal-event-${ev.id}`} onClick={() => onOpen(ev)}
    className={`w-full text-left truncate rounded border px-1.5 py-1 text-[11px] font-medium transition-colors ${STATUS_STYLE[ev.status] || FALLBACK}`}>
    {hhmm(ev._time)} {ev.partenza?.citta || ev.code}
  </button>
);

export const MonthView = ({ anchor, today, eventsForDay, onOpen }) => {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  const firstWeekday = (new Date(y, m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const isToday = (d) => d === today.getDate() && m === today.getMonth() && y === today.getFullYear();
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {WEEKDAYS.map((w) => <div key={w} className="px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, idx) => {
          const dayEvents = d ? eventsForDay(new Date(y, m, d)) : [];
          return (
            <div key={d ? `day-${d}` : `pad-${idx}`} data-testid={d ? `cal-day-${d}` : undefined} className={`min-h-[112px] border-b border-r border-slate-100 p-1.5 ${d ? "" : "bg-slate-50/50"}`}>
              {d && (
                <>
                  <div className={`text-xs font-semibold mb-1 h-6 w-6 flex items-center justify-center rounded-full ${isToday(d) ? "bg-primary text-white" : "text-slate-500"}`}>{d}</div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((ev) => <EventChip key={ev.id} ev={ev} onOpen={onOpen} />)}
                    {dayEvents.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{dayEvents.length - 3} altre</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const WeekView = ({ weekDays, today, eventsForDay, onOpen }) => (
  <Card className="border-slate-200 overflow-hidden">
    <div className="grid grid-cols-7">
      {weekDays.map((d, i) => {
        const isToday = sameDay(d, today);
        const dayEvents = eventsForDay(d);
        return (
          <div key={d.toISOString().slice(0, 10)} data-testid={`cal-week-day-${i}`} className="border-r border-slate-100 last:border-r-0 min-h-[420px]">
            <div className={`px-2 py-2.5 text-center border-b border-slate-200 ${isToday ? "bg-primary/10" : "bg-slate-50"}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{WEEKDAYS[i]}</div>
              <div className={`text-sm font-bold ${isToday ? "text-primary" : "text-slate-700"}`}>{d.getDate()}</div>
            </div>
            <div className="p-1.5 space-y-1">
              {dayEvents.length === 0 ? <div className="text-[10px] text-slate-300 text-center pt-4">—</div> : dayEvents.map((ev) => <EventChip key={ev.id} ev={ev} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  </Card>
);

export const DayView = ({ anchor, eventsForDay, onOpen }) => {
  const dayEvents = eventsForDay(anchor);
  return (
    <Card className="border-slate-200 overflow-hidden">
      {dayEvents.length === 0 ? (
        <div className="py-20 text-center"><CalendarDays className="h-10 w-10 mx-auto text-slate-300 mb-3" /><p className="text-slate-500 text-sm">Nessuna corsa in questa giornata.</p></div>
      ) : (
        <div className="divide-y divide-slate-100">
          {dayEvents.map((ev) => (
            <button key={ev.id} data-testid={`cal-event-${ev.id}`} onClick={() => onOpen(ev)} className="w-full text-left flex items-center gap-4 px-4 py-3.5 hover:bg-slate-50 transition-colors">
              <div className="w-14 text-center"><div className="text-lg font-bold text-slate-800 font-heading">{hhmm(ev._time)}</div></div>
              <div className={`w-1.5 self-stretch rounded-full ${(STATUS_STYLE[ev.status] || "").split(" ")[0]}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{ev.code} · {ev.partenza?.citta || "—"} → {ev.arrivo?.citta || "—"}</p>
                <p className="text-xs text-slate-500 truncate">{ev.operative?.tipologia_mezzo || "—"}{ev.assigned_partner_name ? ` · ${ev.assigned_partner_name}` : ""}</p>
              </div>
              <span className={`text-[11px] font-semibold rounded-full border px-2.5 py-0.5 ${STATUS_STYLE[ev.status] || FALLBACK}`}>{ev.status}</span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};
